import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { MessageCircle, Send, CheckCircle, RefreshCw } from 'lucide-react';
import { questionsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { getImageUrl } from '../utils/helpers';

const SellerQuestions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answerText, setAnswerText] = useState({});
  const [submitting, setSubmitting] = useState({});

  const loadQuestions = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const data = await questionsAPI.getSellerQuestions();
      setQuestions(data);
    } catch (error) {
      console.error('Error loading questions:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las preguntas',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleAnswerQuestion = async (questionId) => {
    const answer = answerText[questionId];
    if (!answer || !answer.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor escribe una respuesta',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(prev => ({ ...prev, [questionId]: true }));

    try {
      await questionsAPI.answerQuestion(questionId, answer.trim());
      
      // Update local state
      setQuestions(prev =>
        prev.map(q =>
          q._id === questionId
            ? { ...q, answer: answer.trim(), answered: true }
            : q
        )
      );

      // Clear answer text
      setAnswerText(prev => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });

      toast({
        title: '¡Respuesta enviada!',
        description: 'El comprador será notificado',
      });
    } catch (error) {
      console.error('Error answering question:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar la respuesta',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(prev => ({ ...prev, [questionId]: false }));
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="p-12 text-center">
          <MessageCircle className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Inicia sesión</h2>
          <p className="text-gray-600 mb-6">
            Debes iniciar sesión para ver las preguntas
          </p>
          <Button onClick={() => navigate('/login')} className="bg-zl-orange hover:bg-zl-orange-600">
            Iniciar sesión
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 mx-auto text-zl-orange animate-spin mb-4" />
          <p className="text-gray-600">Cargando preguntas...</p>
        </div>
      </div>
    );
  }

  const pendingQuestions = questions.filter(q => !q.answered);
  const answeredQuestions = questions.filter(q => q.answered);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Preguntas de compradores</h1>
            <p className="text-gray-600 mt-1">
              {pendingQuestions.length} preguntas pendientes • {answeredQuestions.length} respondidas
            </p>
          </div>
          <Button onClick={loadQuestions} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{questions.length}</p>
                <p className="text-sm text-gray-600">Total preguntas</p>
              </div>
              <MessageCircle className="w-12 h-12 text-zl-orange" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-600">{pendingQuestions.length}</p>
                <p className="text-sm text-gray-600">Sin responder</p>
              </div>
              <MessageCircle className="w-12 h-12 text-orange-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-green-600">{answeredQuestions.length}</p>
                <p className="text-sm text-gray-600">Respondidas</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </Card>
        </div>

        {/* Pending Questions */}
        {pendingQuestions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <MessageCircle className="w-6 h-6 mr-2 text-orange-600" />
              Preguntas pendientes
            </h2>
            <div className="space-y-4">
              {pendingQuestions.map(question => (
                <Card key={question._id} className="p-6 border-l-4 border-orange-500">
                  <div className="flex gap-4">
                    <img
                      src={getImageUrl(question.product_image)}
                      alt={question.product_title}
                      className="w-24 h-24 object-cover rounded cursor-pointer hover:opacity-80"
                      onClick={() => navigate(`/product/${question.product_id}`)}
                      onError={(e) => e.target.src = 'https://via.placeholder.com/96'}
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 
                            className="font-semibold text-lg mb-1 hover:text-zl-orange cursor-pointer"
                            onClick={() => navigate(`/product/${question.product_id}`)}
                          >
                            {question.product_title}
                          </h3>
                          <Badge variant="outline" className="text-orange-600 border-orange-600">
                            Pendiente
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="mb-4 bg-gray-50 p-4 rounded">
                        <p className="font-medium text-gray-900 mb-2">{question.question}</p>
                        <p className="text-sm text-gray-500">
                          Pregunta de <span className="font-medium">{question.user_name}</span> • {formatDate(question.created_at)}
                        </p>
                      </div>

                      <div className="space-y-3">
                        <Textarea
                          placeholder="Escribe tu respuesta..."
                          value={answerText[question._id] || ''}
                          onChange={(e) =>
                            setAnswerText({ ...answerText, [question._id]: e.target.value })
                          }
                          rows={3}
                        />
                        <Button
                          onClick={() => handleAnswerQuestion(question._id)}
                          className="bg-zl-orange hover:bg-zl-orange-600"
                          disabled={submitting[question._id]}
                        >
                          {submitting[question._id] ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Enviar respuesta
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Answered Questions */}
        {answeredQuestions.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
              Preguntas respondidas
            </h2>
            <div className="space-y-4">
              {answeredQuestions.map(question => (
                <Card key={question._id} className="p-6 border-l-4 border-green-500">
                  <div className="flex gap-4">
                    <img
                      src={getImageUrl(question.product_image)}
                      alt={question.product_title}
                      className="w-24 h-24 object-cover rounded cursor-pointer hover:opacity-80"
                      onClick={() => navigate(`/product/${question.product_id}`)}
                      onError={(e) => e.target.src = 'https://via.placeholder.com/96'}
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 
                            className="font-semibold text-lg mb-1 hover:text-zl-orange cursor-pointer"
                            onClick={() => navigate(`/product/${question.product_id}`)}
                          >
                            {question.product_title}
                          </h3>
                          <Badge className="bg-green-600">Respondida</Badge>
                        </div>
                      </div>
                      
                      <div className="mb-3 bg-gray-50 p-4 rounded">
                        <p className="font-medium text-gray-900 mb-2">{question.question}</p>
                        <p className="text-sm text-gray-500">
                          Pregunta de <span className="font-medium">{question.user_name}</span> • {formatDate(question.created_at)}
                        </p>
                      </div>

                      <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                        <p className="text-gray-900">{question.answer}</p>
                        <p className="text-sm text-gray-500 mt-2">Tu respuesta</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {questions.length === 0 && (
          <Card className="p-12 text-center">
            <MessageCircle className="w-24 h-24 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">No tienes preguntas todavía</h2>
            <p className="text-gray-600">
              Cuando los compradores pregunten sobre tus productos, aparecerán aquí
            </p>
          </Card>
        )}
    </div>
  );
};

export default SellerQuestions;
